"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.toRelativePaths = toRelativePaths;
exports.scan = scan;
exports.getExcludedPatterns = getExcludedPatterns;
const fs = require("fs");
const path = require("path");
const path_1 = require("path");
const debug_1 = require("./debug");
const find_1 = require("./find");
const hosted_git_info_1 = require("hosted-git-info");
const signatures_1 = require("./signatures");
const git_1 = require("./git");
const extract_1 = require("./extract");
const fs_1 = require("./utils/fs");
const common_1 = require("./common");
const dotSnyk = require("./utils/dotsnyk");
const invariants_1 = require("./utils/dotsnyk/invariants");
const error_1 = require("./utils/error");
function toRelativePaths(basedir, signatures, extractionWorkspace) {
    signatures.forEach((s) => {
        const src = extractionWorkspace && s.path.includes(extractionWorkspace)
            ? extractionWorkspace
            : basedir;
        s.path = path.relative(src, s.path);
    });
}
async function scan(options) {
    try {
        debug_1.debug.enabled = !!(options === null || options === void 0 ? void 0 : options.debug);
        (0, debug_1.debug)('options %o \n', options);
        const extractionDepthLimit = options['max-depth'] || 0;
        if (extractionDepthLimit < 0) {
            throw 'invalid options: --max-depth should be greater than or equal to 0.';
        }
        if (!options.path) {
            throw 'invalid options: no path provided.';
        }
        if (!fs.existsSync(options.path)) {
            throw `'${options.path}' does not exist.`;
        }
        const start = Date.now();
        const projectRoot = options.path;
        const excludedPatterns = getExcludedPatterns(projectRoot, options['policy-path']);
        if (common_1.isWin) {
            excludedPatterns.forEach((path, index) => {
                excludedPatterns[index] = path.split(path_1.posix.sep).join(path_1.sep);
            });
        }
        const [filePaths, archivePaths] = await (0, find_1.find)(projectRoot, excludedPatterns);
        if (filePaths.length + archivePaths.length == 0) {
            (0, error_1.exitWith)(error_1.ExitCode.NoSupportedFiles, `There were no files in the target directory that could be scanned. Check if the directory is empty or if an ignore policy is active.`);
        }
        let extractionWorkspace = undefined;
        if (0 < extractionDepthLimit && 0 < archivePaths.length) {
            const temporaryDir = await (0, fs_1.createTemporaryDir)();
            extractionWorkspace = (0, path_1.join)(temporaryDir, common_1.DECOMPRESSING_WORKSPACE_DIR);
            await (0, extract_1.extract)(archivePaths, temporaryDir, extractionDepthLimit);
            const [newFilePaths, newArchivePaths] = await (0, find_1.find)(extractionWorkspace, excludedPatterns);
            // NOTE: we used to push + spread here (`arr1.push(...arr2)`), but this caused stack overflows with very large arrays,
            // hence using for loops instead
            for (const file of newFilePaths) {
                filePaths.push(file);
            }
            for (const file of newArchivePaths) {
                filePaths.push(file);
            }
        }
        else {
            for (const archive of archivePaths) {
                filePaths.push(archive);
            }
        }
        (0, debug_1.debug)('%d files found \n', filePaths.length);
        const signatures = await (0, signatures_1.computeSignaturesConcurrently)(filePaths);
        toRelativePaths(projectRoot, signatures, extractionWorkspace);
        const end = Date.now();
        const totalMilliseconds = end - start;
        const totalFileSignatures = signatures.length;
        const totalSecondsElapsedToGenerateFileSignatures = Math.floor(totalMilliseconds / 1000);
        (0, debug_1.debug)(`total fileSignatures: ${totalFileSignatures} \n`);
        (0, debug_1.debug)(`elapsed time in seconds to generate fileSignatures: ${totalSecondsElapsedToGenerateFileSignatures}s \n`);
        const facts = [{ type: 'fileSignatures', data: signatures }];
        const analytics = [
            {
                name: 'fileSignaturesAnalyticsContext',
                data: {
                    totalFileSignatures,
                    totalSecondsElapsedToGenerateFileSignatures,
                },
            },
        ];
        const target = await (0, git_1.getTarget)();
        (0, debug_1.debug)('target %o \n', target);
        const gitInfo = (0, hosted_git_info_1.fromUrl)(target.remoteUrl);
        const name = options['project-name'] || (gitInfo === null || gitInfo === void 0 ? void 0 : gitInfo.project) || path.basename(projectRoot);
        (0, debug_1.debug)('name %o \n', name);
        const targetReference = options['target-reference'];
        if (options['remote-repo-url']) {
            target.remoteUrl = options['remote-repo-url'];
        }
        const scanResults = [
            {
                facts,
                identity: {
                    type: 'cpp',
                },
                name,
                target,
                analytics,
                targetReference,
            },
        ];
        return {
            scanResults,
        };
    }
    catch (err) {
        if (err.code != undefined) {
            throw err;
        }
        throw new Error(`Could not scan C/C++ project: ${err}`);
    }
}
function getExcludedPatterns(projectRoot, policyFilePath = (0, path_1.join)(projectRoot, invariants_1.DEFAULT_SNYK_POLICY_FILE)) {
    var _a, _b;
    if (!dotSnyk.exists(policyFilePath)) {
        return [];
    }
    const config = dotSnyk.parse(policyFilePath);
    const paths = (_b = (_a = config === null || config === void 0 ? void 0 : config.exclude) === null || _a === void 0 ? void 0 : _a.global) === null || _b === void 0 ? void 0 : _b.filter((item) => {
        if (typeof item === 'object') {
            const key = Object.keys(item)[0];
            return !key ? false : !dotSnyk.hasExpired(item[key].expires);
        }
        return true;
    }).map((item) => {
        if (typeof item === 'object') {
            return Object.keys(item)[0];
        }
        return item;
    });
    return [policyFilePath, ...dotSnyk.toAbsolutePaths(projectRoot, paths)];
}
//# sourceMappingURL=scan.js.map